import { ArrowRight } from "lucide-react";
import { fmtN, fmtP, toneText, PaceArrow, PaceBadge, VsLastMonth, VsPrevWeek } from "./Momentum";
import { ProratedBadge } from "@/components/PlanBits";

// Retail first and largest, Lead smallest
const ORDER = [
  { key: "retail", label: "Retail", size: "lg" },
  { key: "booking", label: "Booking", size: "md" },
  { key: "test_drive", label: "Test Drive", size: "md" },
  { key: "enquiry", label: "Enquiry", size: "md" },
  { key: "lead", label: "Lead", size: "sm" },
];
const STEP = { retail: "BOOKING→RETAIL", booking: "TEST DRIVE→BOOKING", test_drive: "ENQUIRY→TEST DRIVE", enquiry: "LEAD→ENQUIRY" };
const SHORT = { LEAD: "Lead", ENQUIRY: "Enq", "TEST DRIVE": "TD", BOOKING: "Bkg", RETAIL: "Retail" };
const fmtDay = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "");

function Conv({ c }) {
  if (!c) return null;
  const tone = c.vs_plan == null ? "text-muted-foreground" : c.vs_plan >= 100 ? "text-emerald-700" : c.vs_plan >= 85 ? "text-amber-600" : "text-red-600";
  return (
    <div className="flex flex-col items-center justify-center gap-y-0 text-[10px] leading-tight px-1 shrink-0 min-w-[58px]" title={`${c.from} → ${c.to}: achieved ${fmtP(c.ach)} vs planned ${fmtP(c.plan)}`} data-testid={`ov-conv-${c.to.toLowerCase().replace(" ", "_")}`}>
      <span className="text-muted-foreground inline-flex items-center gap-0.5 whitespace-nowrap">{SHORT[c.from]}<ArrowRight size={10} />{SHORT[c.to]}</span>
      <span className={`num font-bold text-xs ${tone}`}>{fmtP(c.ach)}</span>
      <span className="text-muted-foreground num whitespace-nowrap">plan {fmtP(c.plan)}</span>
    </div>
  );
}

function Tile({ k, label, size, t, period }) {
  const big = size === "lg";
  const isWeek = period?.kind === "week";
  const future = period?.future;
  const planLbl = isWeek ? "week plan" : "plan";
  const tdLbl = isWeek ? "WTD plan" : "to date";
  return (
    <div className={`border bg-card rounded-md min-w-0 ${big ? "p-4 ring-1 ring-foreground/30 basis-[180px]" : size === "sm" ? "p-2.5 basis-[120px]" : "p-3 basis-[140px]"} flex-1`} data-testid={`ov-tile-${k}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</span>
        <span className="flex items-center gap-1"><ProratedBadge count={t.prorated} testId={`ov-tile-${k}-prorated`} />{!future && <PaceArrow m={t.momentum?.pace7} compact testId={`ov-tile-${k}-pace7`} />}</span>
      </div>
      <div className={`num font-bold leading-none mt-1 ${future ? "text-muted-foreground" : toneText(t.pct)} ${big ? "text-4xl" : size === "sm" ? "text-xl" : "text-2xl"}`} data-testid={`ov-tile-${k}-pct`}>{fmtP(t.pct)}</div>
      <div className="text-[11px] mt-1.5 num"><b data-testid={`ov-tile-${k}-ach`}>{fmtN(t.ach)}</b> <span className="text-muted-foreground">of <span data-testid={`ov-tile-${k}-ptd`}>{fmtN(t.plan_td, 1)}</span> {tdLbl} · {planLbl} <span data-testid={`ov-tile-${k}-plan`}>{fmtN(t.plan_full, 1)}</span></span></div>
      {future ? (
        <div className="text-[10px] text-muted-foreground mt-0.5" data-testid={`ov-tile-${k}-future`}>Week not started — plan {fmtN(t.plan_full, 1)}, achievement —</div>
      ) : (
        <div className="text-[10px] text-muted-foreground num mt-0.5">
          Gap {fmtN(t.gap, 1)}
          {(!isWeek || period?.current) && <> · {isWeek ? "proj. week-end" : "proj"} <b className="text-foreground" data-testid={`ov-tile-${k}-proj`}>{fmtN(t.projected)}</b> · need <b className="text-foreground" data-testid={`ov-tile-${k}-req`}>{fmtN(t.required_rate, 1)}</b>/day</>}
          {" · "}{isWeek ? "week" : "month"} {fmtP(t.full_pct)}
        </div>
      )}
      {!future && (
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
          {(!isWeek || period?.current) && <PaceBadge p={t.momentum?.pace_vs_plan} testId={`ov-tile-${k}-pace-plan`} />}
          {isWeek ? <VsPrevWeek v={t.momentum?.vs_prev_week} testId={`ov-tile-${k}-vs-pw`} /> : <VsLastMonth v={t.momentum?.vs_last_month} testId={`ov-tile-${k}-vs-lm`} />}
        </div>
      )}
    </div>
  );
}

export function FunnelTiles({ funnel, conversions, period }) {
  const conv = Object.fromEntries(conversions.map((c) => [c.step, c]));
  const enqRet = conv["ENQUIRY→RETAIL"];
  const isWeek = period?.kind === "week";
  const title = isWeek ? `A · Funnel — ${period.week} (${fmtDay(period.start)} – ${fmtDay(period.end)})${period.current ? " · current week" : period.future ? " · not started" : " · completed"}` : "A · Funnel — month-to-date";
  return (
    <div data-testid="ov-funnel">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading text-sm font-semibold" data-testid="ov-funnel-title">{title}</h3>
        {enqRet && !period?.future && <span className="text-[11px] text-muted-foreground num" data-testid="ov-conv-enq-retail">Enquiry→Retail <b className={enqRet.vs_plan == null ? "" : enqRet.vs_plan >= 100 ? "text-emerald-700" : enqRet.vs_plan >= 85 ? "text-amber-600" : "text-red-600"}>{fmtP(enqRet.ach)}</b> vs plan {fmtP(enqRet.plan)}</span>}
      </div>
      <div className="flex flex-wrap items-stretch gap-1 min-w-0" data-testid="ov-tiles">
        {ORDER.map((o, i) => (
          <div key={o.key} className="flex items-center gap-1 flex-1 min-w-[200px] sm:min-w-[230px]">
            <Tile k={o.key} label={o.label} size={o.size} t={funnel[o.key]} period={period} />
            {i < ORDER.length - 1 && !period?.future && <Conv c={conv[STEP[o.key]]} />}
          </div>
        ))}
      </div>
      <p className="text-[10px] text-muted-foreground mt-1.5">
        {isWeek
          ? "Ach % = week Ach ÷ WTD plan (completed week: the full week plan; running week: week plan × working days elapsed ÷ working days in the week) · ▲▶▼ = today vs the 3-day average (±10 %) · pace badge = 3-day rate ÷ required daily rate for the rest of the week · vs previous week = Ach-to-date against the previous week at the same working-day count"
          : "Ach % = Ach MTD ÷ MTD plan (approved week plans: completed weeks in full + current week by working days elapsed; plans without a week split are pro-rated and badged) · arrows between tiles = achieved stage conversion vs the ratio implied by the plan · ▲▶▼ = 7-day pace vs the previous 7 working days (±10 %) · pace badge = last-7-day rate ÷ required daily rate"}
      </p>
    </div>
  );
}
